import { BASE_URL } from '../helpers/constants';

class PushNotificationService {
  constructor() {
    this.swRegistration = null;
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  }

  async init() {
    if (!this.isSupported) {
      return false;
    }

    try {
      // First, unregister any OTHER service workers (not our push worker)
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        if (!registration.active || !registration.active.scriptURL.includes('push-service-worker.js')) {
          await registration.unregister();
        }
      }
      
      // Use our custom push service worker
      const swUrl = '/push-service-worker.js';
      
      this.swRegistration = await navigator.serviceWorker.register(swUrl, {
        scope: './',
        updateViaCache: 'none'
      });
      
      // Wait for the service worker to be active
      await new Promise((resolve) => {
        if (this.swRegistration.active) {
          resolve();
        } else {
          this.swRegistration.addEventListener('updatefound', () => {
            const newWorker = this.swRegistration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'activated') {
                resolve();
              }
            });
          });
        }
      });
      
      // Get the ready registration
      this.swRegistration = await navigator.serviceWorker.ready;
      
      // Check if push is actually supported
      if (!this.swRegistration.pushManager) {
        console.error('PushManager not available in service worker registration');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }
  }

  async getVapidPublicKey(token) {
    try {
      const response = await fetch(`${BASE_URL}push-subscriptions/vapid-public-key`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('VAPID key fetch failed:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.publicKey) {
        throw new Error('No public key in response');
      }
      
      // Validate VAPID key format (should be URL-safe base64)
      const urlSafeBase64Regex = /^[A-Za-z0-9_-]+$/;
      if (!urlSafeBase64Regex.test(data.publicKey)) {
        console.error('Invalid VAPID key format:', data.publicKey);
        throw new Error('VAPID public key has invalid format');
      }
      
      return data.publicKey;
    } catch (error) {
      console.error('Failed to get VAPID public key:', error);
      throw error;
    }
  }

  async requestPermission() {
    if (!this.isSupported) return 'unsupported';
    
    const permission = await Notification.requestPermission();
    return permission;
  }

  async subscribe(token) {
    if (!this.swRegistration) {
      throw new Error('Service Worker not registered');
    }

    try {
      // Get VAPID public key
      const vapidPublicKey = await this.getVapidPublicKey(token);
      const convertedVapidKey = this.urlBase64ToUint8Array(vapidPublicKey);

      // Check existing subscription
      const existingSubscription = await this.swRegistration.pushManager.getSubscription();
      if (existingSubscription) {
        await existingSubscription.unsubscribe();
      }

      // Create new subscription
      let subscription;
      try {
        subscription = await this.swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedVapidKey
        });
      } catch (subscribeError) {
        console.error('PushManager.subscribe failed:', subscribeError);
        
        // Handle specific error types
        if (subscribeError.name === 'AbortError') {
          if (subscribeError.message.includes('storage error')) {
            // Storage error - this is a Chrome bug that requires manual intervention
            throw new Error('Chrome storage error detected. Please follow these steps:\n1. Open chrome://settings/content/all\n2. Find localhost:3000 and click the trash icon\n3. Restart Chrome\n4. Try again');
          } else if (subscribeError.message.includes('push service error')) {
            throw new Error('Push service is unreachable. This often happens in development environments. Try using HTTPS (e.g., with ngrok) or check your internet connection.');
          }
        } else if (subscribeError.name === 'InvalidStateError') {
          throw new Error('Push messaging is not supported or disabled in your browser');
        } else if (subscribeError.name === 'InvalidAccessError') {
          throw new Error('Invalid VAPID public key');
        } else if (subscribeError.name === 'NotAllowedError') {
          throw new Error('Permission for push notifications was denied');
        } else {
          throw new Error(`Push subscription failed: ${subscribeError.message}`);
        }
      }

      // Send subscription to backend
      await this.sendSubscriptionToBackend(subscription, token);
      
      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  async unsubscribe(token) {
    if (!this.swRegistration) {
      throw new Error('Service Worker not registered');
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        // Unsubscribe from backend first
        await this.removeSubscriptionFromBackend(subscription, token);
        
        // Then unsubscribe from browser
        await subscription.unsubscribe();
      }
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      throw error;
    }
  }

  async getSubscription() {
    if (!this.swRegistration) return null;
    return await this.swRegistration.pushManager.getSubscription();
  }

  async sendSubscriptionToBackend(subscription, token) {
    const subscriptionData = {
      endpoint: subscription.endpoint,
      p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
      auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
    };

    const response = await fetch(`${BASE_URL}push-subscriptions/subscribe`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(subscriptionData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
  }

  async removeSubscriptionFromBackend(subscription, token) {
    const response = await fetch(`${BASE_URL}push-subscriptions/unsubscribe`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ endpoint: subscription.endpoint })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
  }

  urlBase64ToUint8Array(base64String) {
    if (!base64String) {
      throw new Error('VAPID public key is empty or undefined');
    }
    
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    // Validate the key length (should be 65 bytes for P-256 curve)
    if (outputArray.length !== 65) {
      console.warn(`VAPID key length is ${outputArray.length}, expected 65`);
    }
    
    return outputArray;
  }
}

export default new PushNotificationService();